import React, { useEffect, useState } from "react";
import { db } from "../../firebase/config";
import { doc, getDoc, setDoc, arrayUnion, arrayRemove } from "firebase/firestore";

const AdminSettings = () => {
  const [deliveryCharge, setDeliveryCharge] = useState("");
  const [loading, setLoading] = useState(false);
  const [areas, setAreas] = useState([]);
  const [newArea, setNewArea] = useState("");
  const [areaLoading, setAreaLoading] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [announcementLoading, setAnnouncementLoading] = useState(true);
  const [announcementSaving, setAnnouncementSaving] = useState(false);

  useEffect(() => {
    const fetchSetting = async () => {
      const docRef = doc(db, "app_settings", "general");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setDeliveryCharge(docSnap.data().deliveryChargePerOrder || "");
      }
    };
    fetchSetting();
  }, []);

  useEffect(() => {
    const fetchAreas = async () => {
      const docRef = doc(db, "app_settings", "areas");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setAreas(docSnap.data().list || []);
      }
    };
    fetchAreas();
  }, []);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      setAnnouncementLoading(true);
      try {
        const docRef = doc(db, "settings", "announcement");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setAnnouncement(docSnap.data().message || "");
        }
      } catch (err) {}
      setAnnouncementLoading(false);
    };
    fetchAnnouncement();
  }, []);

  const updateDeliveryCharge = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, "app_settings", "general"), {
        deliveryChargePerOrder: Number(deliveryCharge),
      }, { merge: true });
      alert("✅ Delivery charge updated!");
    } catch (err) {
      alert("❌ Failed to update");
    }
    setLoading(false);
  };

  const addArea = async () => {
    if (!newArea.trim()) return;
    setAreaLoading(true);
    try {
      const updatedAreas = [...areas, newArea.trim()];
      await setDoc(doc(db, "app_settings", "areas"), { list: updatedAreas }, { merge: true });
      setAreas(updatedAreas);
      setNewArea("");
    } catch (err) {
      alert("❌ Failed to add area");
    }
    setAreaLoading(false);
  };

  const deleteArea = async (areaToDelete) => {
    setAreaLoading(true);
    try {
      const updatedAreas = areas.filter(a => a !== areaToDelete);
      await setDoc(doc(db, "app_settings", "areas"), { list: updatedAreas }, { merge: true });
      setAreas(updatedAreas);
    } catch (err) {
      alert("❌ Failed to delete area");
    }
    setAreaLoading(false);
  };

  const saveAnnouncement = async () => {
    setAnnouncementSaving(true);
    try {
      await setDoc(doc(db, "settings", "announcement"), { message: announcement });
      alert("✅ Announcement updated!");
    } catch (err) {
      alert("❌ Failed to update announcement");
    }
    setAnnouncementSaving(false);
  };

  return (
    <div className="p-6 max-w-xl mx-auto">
      <button
        onClick={() => window.location.href = '/admin/dashboard'}
        className="mb-4 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
      >
        ← Back to Dashboard
      </button>
      <h2 className="text-2xl font-semibold mb-4">⚙️ App Settings</h2>
      <div className="space-y-4">
        <div>
          <label className="block font-medium">Delivery Charge Per Order (₹)</label>
          <input
            type="number"
            className="w-full border p-2 rounded"
            value={deliveryCharge}
            onChange={(e) => setDeliveryCharge(e.target.value)}
          />
        </div>
        <button
          onClick={updateDeliveryCharge}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          disabled={loading}
        >
          {loading ? "Updating..." : "💾 Save Changes"}
        </button>
      </div>
      <div className="mt-10">
        <h3 className="text-xl font-semibold mb-2">🗺️ Manage Serviceable Areas</h3>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            className="border p-2 rounded w-full"
            placeholder="Add new area"
            value={newArea}
            onChange={e => setNewArea(e.target.value)}
            disabled={areaLoading}
          />
          <button
            onClick={addArea}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            disabled={areaLoading}
          >
            Add
          </button>
        </div>
        <ul className="space-y-2">
          {areas.map(area => (
            <li key={area} className="flex items-center justify-between bg-gray-50 p-2 rounded">
              <span>{area}</span>
              <button
                onClick={() => deleteArea(area)}
                className="text-red-600 hover:underline text-sm"
                disabled={areaLoading}
              >
                Delete
              </button>
            </li>
          ))}
          {areas.length === 0 && <li className="text-gray-500">No areas added yet.</li>}
        </ul>
      </div>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-yellow-800 mb-2">📢 Homepage Announcement Banner</h3>
        {announcementLoading ? (
          <div className="text-yellow-700">Loading...</div>
        ) : (
          <>
            <textarea
              className="w-full border p-2 rounded mb-2"
              rows={2}
              value={announcement}
              onChange={e => setAnnouncement(e.target.value)}
              placeholder="Enter announcement, offer, or coupon message..."
            />
            <button
              onClick={saveAnnouncement}
              disabled={announcementSaving}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded"
            >
              {announcementSaving ? "Saving..." : "Save Announcement"}
            </button>
            <div className="mt-2 text-yellow-900 text-sm">
              <b>Preview:</b> {announcement || <span className="italic text-gray-400">No announcement set</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSettings; 